import { Component } from '@theme/component';

class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content', 'marqueeItems'];

  #resizeObserver = null;
  #marqueeWidth = null;
  #marqueeItemsWidth = null;

  connectedCallback() {
    super.connectedCallback();

    const { marqueeItems } = this.refs;
    if (marqueeItems.length === 0) return;

    this.#setupObserver();
    this.#updateMarquee(true);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#setupObserver();
    this.#updateMarquee(true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
    }
  }

  #setupObserver() {
    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
    }

    const { content } = this.refs;
    this.#resizeObserver = new ResizeObserver(() => {
      this.#updateMarquee();
    });

    this.#resizeObserver.observe(this);
    if (content) {
      this.#resizeObserver.observe(content);
    }
  }

  #updateMarquee(force = false) {
    const { marqueeItems, content } = this.refs;
    if (!marqueeItems[0]) return;

    const marqueeWidth = this.offsetWidth;
    const marqueeItemsWidth = marqueeItems[0].offsetWidth;

    if (marqueeWidth === 0 || marqueeItemsWidth === 0) return;

    // Only return early if not forced AND both widths are unchanged
    if (!force && this.#marqueeWidth === marqueeWidth && this.#marqueeItemsWidth === marqueeItemsWidth) {
      return;
    }

    this.#marqueeWidth = marqueeWidth;
    this.#marqueeItemsWidth = marqueeItemsWidth;

    const numberOfCopies = Math.ceil(marqueeWidth / marqueeItemsWidth) + 1;
    const speed = this.#calculateSpeed(numberOfCopies);

    const currentNumberOfCopies = this.refs.content.querySelectorAll('[ref="marqueeItems[]"]').length || marqueeItems.length;

    if (numberOfCopies > currentNumberOfCopies) {
      this.#addRepeatedItems(numberOfCopies - currentNumberOfCopies);
    } else if (numberOfCopies < currentNumberOfCopies) {
      this.#removeRepeatedItems(currentNumberOfCopies - numberOfCopies);
    }

    this.#duplicateContent();
    this.#setSpeed(speed);
  }

  #calculateSpeed(numberOfCopies) {
    const speedFactor = Number(this.getAttribute('data-speed-factor')) || 25;
    return Math.sqrt(numberOfCopies) * speedFactor;
  }

  #setSpeed(value) {
    this.style.setProperty('--marquee-speed', `${value}s`);
  }

  #duplicateContent() {
    this.clonedContent?.remove();

    const clone = /** @type {HTMLElement} */ (this.refs.content.cloneNode(true));
    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');

    this.refs.wrapper.appendChild(clone);
  }

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;
    return content !== lastChild ? lastChild : null;
  }

  #addRepeatedItems(count) {
    const { content, marqueeItems } = this.refs;
    if (!marqueeItems[0]) return;

    for (let i = 0; i < count; i++) {
      const clone = marqueeItems[0].cloneNode(true);
      content.appendChild(clone);
    }
  }

  #removeRepeatedItems(count) {
    const { content } = this.refs;
    const children = Array.from(content.children);
    const itemsToRemove = Math.min(count, children.length - 1);

    for (let i = 0; i < itemsToRemove; i++) {
      content.lastElementChild?.remove();
    }
  }
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}
